import { Formik } from 'formik';
import React, { FunctionComponent, useCallback } from 'react';
import { ContactFormFields } from './contact-form-fields-type';
import ContactFormView from './contact-form-view';
import { ContactFormModalProps } from './contact-form-modal';
import { PhoneNumber, ToastError, ToastSuccess } from 'utils';
import contactFormValidationSchema from './contact-form-validation-schema';
import { capitalizeName } from 'utils/capitalize';
import { ContactResponse, UpdateContactRequest } from 'generated/api-client';

type ContactFormUpdateContainerProps = ContactFormModalProps & {
  contactId: number;
  contact?: ContactResponse;
  updateContact: (contact: UpdateContactRequest) => Promise<void>;
};

const ContactFormUpdateContainer: FunctionComponent<
  ContactFormUpdateContainerProps
> = ({ contact, updateContact, onClose, contactId, ...rest }) => {
  const onSubmit = useCallback(
    async (values: ContactFormFields) => {
      await updateContact({
        givenName: capitalizeName(values.givenName).trim(),
        familyName: capitalizeName(values.familyName).trim(),
        phoneNumber: values.phoneNumber
          ? PhoneNumber.toE164(values.phoneNumber)
          : null,
        email: values.email.toLowerCase().trim() || null,
      }).catch((error) => {
        ToastError('Erreur', "Impossible d'enregistrer le contact");
        throw error;
      });

      if (onClose) {
        onClose();
      }

      ToastSuccess('Succès', 'Contact modifié');
    },
    [updateContact],
  );

  // Loading state
  if (!contact) {
    return null;
  }

  const initialValues: ContactFormFields = {
    givenName: contact.givenName,
    familyName: contact.familyName,
    phoneNumber: contact.phoneNumber || '',
    email: contact.email || '',
  };

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={contactFormValidationSchema}
      validateOnChange={false}
    >
      <ContactFormView onClose={onClose} contactId={contactId} {...rest} />
    </Formik>
  );
};

export default ContactFormUpdateContainer;
